import { useEffect, useState } from 'react';
import type { ServiceCode } from '@common/enums';
import type { PatientInsuranceHistory } from '@common/interfaces/api/patientsApi';
import type { PatientHasContractedCareForService } from '@common/interfaces/patientInsurance';
import type { ICustomControlRequestAddEditProps } from '@frontend/components/pages/customControls/interfaces/customControlRequestAddEditProps';
import { CustomControlMode } from '@common/enums/serviceWorkflow/customControlMode';
import {
    getActiveInsuranceFromInsuranceHistory,
    getPatientHasContractedCare,
} from '@frontend/services/api/patientInsuranceApiService';
import { useEpisodeContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { InsuranceAlert } from './InsuranceAlert';
import { InsuranceModal } from './InsuranceModal';

type PatientInsuranceOldCarepathProps = Readonly<{
    handleChangeCustomControl?: ICustomControlRequestAddEditProps['handleChangeCustomControl'];
    isPatientInformed?: boolean;
    mode: CustomControlMode;
    onCancel?: () => void;
    serviceCode: ServiceCode;
}>;

export function PatientInsuranceOldCarepath(props: PatientInsuranceOldCarepathProps) {
    const { isPatientInformed, mode, onCancel, serviceCode } = props;
    const abortSignal = useAbortSignal();
    const { patient } = useEpisodeContext();
    const [patientInsurance, setPatientInsurance] = useState<PatientHasContractedCareForService>();
    const [activeInsurance, setActiveInsurance] = useState<PatientInsuranceHistory | null>();
    const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(
        !isPatientInformed && mode === CustomControlMode.edit
    );

    useEffect(() => {
        getPatientInsurance(serviceCode, patient.id);
        getActiveInsurance(patient.id);
    }, [patient.id, serviceCode]);

    return (
        <>
            <InsuranceModal
                activeInsurance={activeInsurance}
                isOpen={isInsuranceModalOpen}
                isPatientInformed={isPatientInformed || mode === CustomControlMode.read}
                mode={mode}
                onCancel={onCancel && mode === CustomControlMode.edit ? onCancel : () => {}}
                onChange={props.handleChangeCustomControl}
                onClose={() => setIsInsuranceModalOpen(false)}
                patient={patient}
                patientInsurance={patientInsurance}
                serviceCode={serviceCode}
            />

            <InsuranceAlert
                onClick={() => setIsInsuranceModalOpen(true)}
                patient={patient}
                patientInsurance={patientInsurance}
            />
        </>
    );

    async function getPatientInsurance(code: ServiceCode, patientId: string) {
        const result = await getPatientHasContractedCare(code, patientId, abortSignal);

        setPatientInsurance(result);
    }

    async function getActiveInsurance(patientId: string) {
        const result = await getActiveInsuranceFromInsuranceHistory(patientId, abortSignal);

        setActiveInsurance(result);
    }
}
